import {ActivityFlightState} from "../../types-enums/activity-flight";
import {AirlineResponse, AirportResponse, FlightCalculationRequestLeg} from "../../api/model/activity-flight";
import {
    CalculatedEmissionsResponse,
    CarBusCalculatedEmissionsResponse,
    FlightCalculatedEmissionsResponse,
    HotelCalculatedEmissionsResponse
} from "../../api/model/emissions";
import {CALCULATION_TYPE, GeneralBackendResponse, NonCalculationResponseError} from "../../api/model/common";
import {ActivityHotelState} from "../../types-enums/activity-hotel";
import {CityResponse, CountryResponse, HotelCalculationRequest, HotelResponse} from "../../api/model/activity-hotel";
import {Customer, CustomerCompany} from "../../api/model/customer";
import {
    CreateBundleSuccessResponse,
    CreateOrUpdateBundleMultipleRowActivityResponse,
    CreateOrUpdateBundleSingleRowActivityResponse,
    LoadBundleCarBusActivity,
    LoadBundleFlightActivity,
    LoadBundleHotelActivity,
    LoadedActivity,
    UpdateBundle
} from "../../api/model/bundle";
import {
    ActivityWithDistanceProps,
    ActivityWithReturnRouteProps,
    DatesProps
} from "../../types-enums/activity-common-properties";
import {GeocodingResponse} from "@mapbox/search-js-core/dist/geocode/GeocodingCore";
import {GeocodingFeature} from "@mapbox/search-js-core/dist/geocode/types";
import {ActivityCarBusState} from "../../types-enums/activity-car-bus";
import {CarBusCalculationRequest} from "../../api/model/activity-car-bus";
import {AgencyProps, WindowedAgencyProps} from "../../api/model/agency";

//////////////////
// Redux states //
/////////////////

export const isActivityFlightState = (item: any): item is ActivityFlightState =>
    typeof item === 'object' &&
    item !== null &&
    'legs' in item &&
    'passengerCount' in item &&
    'returnId' in item

export const isActivityHotelState = (item: any): item is ActivityHotelState =>
    typeof item === 'object' &&
    item !== null &&
    'city' in item &&
    'country' in item &&
    'hotel' in item &&
    'guestCount' in item

export const isActivityCarBusState = (item: any): item is ActivityCarBusState =>
    typeof item === 'object' &&
    item !== null &&
    'vehicleType' in item &&
    'fuelType' in item &&
    'manualEnteredDistanceKm' in item &&
    'calculatedDistanceKm' in item &&
    'fuelConsumption' in item &&
    'carCategory' in item &&
    'busCategory' in item &&
    'acrissCode' in item &&
    'countDays' in item &&
    'passengerCount' in item &&
    'waypoints' in item &&
    "route" in item


/////////////////////
// API type guards //
/////////////////////

export const isCreateOrUpdateBundleMultipleRowActivityResponse = (item: any): item is CreateOrUpdateBundleMultipleRowActivityResponse =>
    typeof item === 'object' &&
    item !== null &&
    'frontendIds' in item &&
    'backendId' in item &&
    'groupId' in item

export const isCreateOrUpdateBundleSingleRowActivityResponse = (item: any): item is CreateOrUpdateBundleSingleRowActivityResponse =>
    typeof item === 'object' &&
    item !== null &&
    'frontendIds' in item &&
    'backendId' in item

export const isHotelCalculationRequest = (item: any): item is HotelCalculationRequest =>
    typeof item === 'object' &&
    item !== null &&
    'index' in item &&
    'hotelName' in item &&
    'city' in item &&
    'region' in item &&
    'country' in item &&
    'hotelClass' in item &&
    'roomCategory' in item &&
    'numberOfRooms' in item &&
    'numberOfNights' in item &&
    'numberOfPeople' in item &&
    'mealPlan' in item &&
    item.type === CALCULATION_TYPE.hotel


export const isCarBusCalculationRequest = (item: any): item is CarBusCalculationRequest =>
    typeof item === 'object' &&
    item !== null &&
    'index' in item &&
    'vehicleType' in item &&
    'numberOfPassengers' in item &&
    'fuelConsumption' in item &&
    'distance' in item &&
    'daysOfUse' in item &&
    'fuelType' in item &&
    'carInfo' in item &&
    'busCategory' in item


export const isCreateBundleSuccessResponse = (item: any): item is CreateBundleSuccessResponse =>
    typeof item === 'object' &&
    item !== null &&
    'bundleId' in item &&
    'mapOfActivityIds' in item

export const isUpdateBundle = (item: any): item is UpdateBundle =>
    typeof item === 'object' &&
    item !== null &&
    'name' in item &&
    'description' in item &&
    'journeyDuration' in item &&
    'customer' in item &&
    'agency' in item &&
    'activities' in item &&
    'id' in item

export const isFlightCalculationRequestLeg = (item: any): item is FlightCalculationRequestLeg => typeof item === 'object' &&
    item !== null &&
    'index' in item &&
    'departure' in item &&
    'arrival' in item &&
    'passengerCount' in item

export const isLoadBundleFlightActivity = (item: any): item is LoadBundleFlightActivity => typeof item === 'object' &&
    item !== null &&
    "departureName" in item &&
    "departureIata" in item &&
    "departureCountryAlpha3" in item &&
    "arrivalName" in item &&
    "arrivalIata" in item &&
    "arrivalCountryAlpha3" in item &&
    "airlineName" in item &&
    "airlineIata" in item &&
    "aircraftName" in item &&
    "aircraftIata" in item &&
    "flightNumber" in item &&
    "flightDate" in item &&
    "flightCount" in item &&
    "passengerCount" in item &&
    "travelClass" in item &&
    "groupId" in item &&
    "activityType" in item &&
    "flightNumberDiscontinued" in item &&
    "originalAirportInformation" in item &&
    "index" in item &&
    "returnFlightId" in item &&
    "emissions" in item &&
    "id" in item &&
    "updated" in item &&
    "day" in item

export const isLoadBundleHotelActivity = (item: any): item is LoadBundleHotelActivity => typeof item === 'object' &&
    item !== null &&
    "countryAlpha2" in item &&
    "countryAlpha3" in item &&
    "countryName" in item &&
    "giataId" in item &&
    "hotelName" in item &&
    "city" in item &&
    "region" in item &&
    "hotelClass" in item &&
    "roomCategory" in item &&
    "numberRooms" in item &&
    "numberNights" in item &&
    "numberPeople" in item &&
    "mealPlan" in item &&
    "activityType" in item &&
    "hotelDiscontinued" in item &&
    "originalHotelName" in item &&
    "originalHotelClass" in item &&
    "emissions" in item &&
    "id" in item &&
    "updated" in item &&
    "day" in item

export const isLoadBundleCarBusActivity = (item: any): item is LoadBundleCarBusActivity => typeof item === 'object' &&
    item !== null &&
    'calculatedDistanceKm' in item &&
    'manualEnteredDistanceKm' in item &&
    'vehicleType' in item &&
    'fuelConsumption' in item &&
    'fuelType' in item &&
    'busCategory' in item &&
    'numberOfPassengers' in item &&
    'carInfo' in item &&
    'isHybrid' in item &&
    'emissions' in item &&
    "route" in item &&
    'waypoints' in item &&
    'daysOfUse' in item &&
    "updated" in item &&
    "day" in item &&
    "id" in item

export const isGeocodingResponse = (item: any): item is GeocodingResponse => typeof item === 'object' &&
    item !== null &&
    "type" in item &&
    "features" in item &&
    "attribution" in item &&
    "url" in item

export const isGeocodingFeature = (item: any): item is GeocodingFeature => typeof item === 'object' &&
    item !== null &&
    "type" in item &&
    "id" in item &&
    "geometry" in item &&
    "properties" in item

export const isAirlineResponse = (item: any): item is AirlineResponse => typeof item === 'object' &&
    item !== null &&
    "name" in item &&
    "id" in item &&
    "iata" in item

export const isJourneyDatesProps = (item: any): item is DatesProps => typeof item === 'object' &&
    item !== null &&
    "startDate" in item &&
    "endDate" in item

export const isAirportResponse = (item: any): item is AirportResponse => typeof item === 'object' &&
    item !== null &&
    "name" in item &&
    "id" in item &&
    "iata" in item &&
    "country" in item

/**
 * General backend response containing a property flightActivities. In flightActivities, there is an array of FlightCalculatedEmissionsResponse
 */
export const isGeneralCalculatedFlightEmissionsResponse = (item: any): item is CalculatedEmissionsResponse => typeof item === 'object' &&
    item !== null &&
    item.data !== null &&
    typeof item.data === 'object' &&
    'flightActivities' in item.data &&
    'status' in item?.data &&
    'emissionKgCO2eTotal' in item?.data &&
    'errorCodes' in item?.data

export const isFlightCalculatedEmissionsResponse = (item: any): item is FlightCalculatedEmissionsResponse => typeof item === 'object' &&
    item !== null &&
    "index" in item &&
    "status" in item &&
    "emissionKgCO2e" in item &&
    "errorCodes" in item &&
    "errors" in item &&
    "type" in item &&
    item.type === CALCULATION_TYPE.flight


/**
 * General backend response containing a property hotelActivities. In hotelActivities, there is an array of HotelCalculatedEmissionsResponse
 */
export const isGeneralCalculatedHotelEmissionsResponse = (item: any): item is CalculatedEmissionsResponse => typeof item === 'object' &&
    item !== null &&
    item.data !== null &&
    typeof item.data === 'object' &&
    'hotelActivities' in item.data &&
    'status' in item?.data &&
    'emissionKgCO2eTotal' in item?.data &&
    'errorCodes' in item?.data

export const isHotelCalculatedEmissionsResponse = (item: any): item is HotelCalculatedEmissionsResponse => typeof item === 'object' &&
    item !== null &&
    "index" in item &&
    "status" in item &&
    "emissionKgCO2e" in item &&
    "errorCodes" in item &&
    "errors" in item &&
    "scope" in item &&
    "type" in item &&
    item.type === CALCULATION_TYPE.hotel

/**
 * General backend response containing a property carActivities. In carActivities, there is an array of CarBusCalculatedEmissionsResponse
 */
export const isGeneralCalculatedCarBusEmissionsResponse = (item: any): item is CalculatedEmissionsResponse => typeof item === 'object' &&
    item !== null &&
    item.data !== null &&
    typeof item.data === 'object' &&
    'carActivities' in item.data &&
    'status' in item?.data &&
    'emissionKgCO2eTotal' in item?.data &&
    'errorCodes' in item?.data

export const isCalculatedCarBusEmissionsResponse = (item: any): item is CarBusCalculatedEmissionsResponse =>
    typeof item === 'object' &&
    item !== null &&
    "index" in item &&
    "status" in item &&
    "emissionKgCO2e" in item &&
    "errorCodes" in item &&
    "type" in item &&
    (item.type === CALCULATION_TYPE.car || item.type === CALCULATION_TYPE.bus);

export const isNonCalculationErrorResponse = (item: any): item is NonCalculationResponseError =>
    typeof item === 'object' &&
    item !== null &&
    'status' in item &&
    'data' in item &&
    item.data !== null &&
    typeof item.data === 'object' &&
    'errorCode' in item.data &&
    'message' in item.data &&
    'problematicValues' in item.data &&
    'type' in item.data;

export const isHotelResponse = (item: any): item is HotelResponse => typeof item === 'object' &&
    item !== null &&
    "name" in item &&
    "id" in item &&
    "city" in item &&
    "alpha2" in item &&
    "alpha3" in item &&
    "countryName" in item &&
    "stars" in item

export const isCityResponse = (item: any): item is CityResponse => typeof item === 'object' &&
    item !== null &&
    "name" in item &&
    "id" in item &&
    "alpha2" in item &&
    "alpha3" in item &&
    "countryName" in item &&
    !("stars" in item);

export const isCountryResponse = (item: any): item is CountryResponse => typeof item === 'object' &&
    item !== null &&
    "name" in item &&
    "id" in item &&
    "alpha2" in item &&
    "alpha3" in item &&
    !("countryName" in item)

export const isFormCustomer = (item: any): item is Customer => typeof item === 'object' &&
    item !== null &&
    'firstName' in item &&
    'lastName' in item &&
    'company' in item &&
    'email' in item

export const isCustomerCompanyResponse = (item: any): item is CustomerCompany => typeof item === 'object' &&
    item !== null &&
    'id' in item &&
    'name' in item

export const isEmptyHotelResponse = (item: any): item is HotelResponse => isHotelResponse(item) &&
    item.name === '' &&
    item.city === '' &&
    item.region === null &&
    item.alpha2 === '' &&
    item.alpha3 === '' &&
    item.countryName === '' &&
    item.stars === ''

export const isEmptyCityResponse = (item: any): item is CityResponse => isCityResponse(item) &&
    item.name === '' &&
    item.region === null &&
    item.alpha2 === '' &&
    item.alpha3 === '' &&
    item.countryName === ''

export const isGeneralBackendResponseWithErrorCode = (item: any): item is GeneralBackendResponse => typeof item === 'object' &&
    item !== null &&
    "status" in item &&
    "errorCode" in item &&
    "type" in item

export const isAgencyProps = (item: any): item is AgencyProps => typeof item === "object" &&
    item !== null &&
    "memberIds" in item &&
    "adminIds" in item

export const isWindowedAgencyProps = (item: any): item is WindowedAgencyProps => typeof item === "object" &&
    item !== null &&
    "members" in item &&
    "admins" in item


////////////
// Mixed //
//////////

export const isActivityWithReturnRouteProps = (item: any): item is ActivityWithReturnRouteProps => typeof item === 'object' &&
    item !== null &&
    'returnId' in item

export const isActivityWithDistanceProps = (item: any): item is ActivityWithDistanceProps => typeof item === 'object' &&
    item !== null &&
    'manualEnteredDistanceKm' in item &&
    'calculatedDistanceKm' in item

export const isDefined = <T>(value: T | undefined | null): value is T => value !== undefined && value !== null;

export const isCustomer = (item: any): item is Customer => typeof item === 'object' &&
    item !== null &&
    'firstName' in item &&
    'lastName' in item &&
    'company' in item &&
    'email' in item &&
    'street' in item &&
    'houseNumber' in item &&
    'city' in item &&
    'zipCode' in item &&
    'phoneNumber' in item

export const isLoadedActivity = (item: any): item is LoadedActivity => typeof item === 'object' &&
    item !== null &&
    "emissions" in item &&
    "id" in item &&
    "updated" in item &&
    "day" in item &&
    "activityType" in item &&
    "previousEmissionsCo2KgE" in item